import { useMemo } from 'react'
import CloseIcon from '@mui/icons-material/Close'
import { Controller, useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import { AddressState } from 'types'
import { todayDateInputValue, validateFutureExpiration } from '../expirationDate'
import { Field } from '../Field'
import { FileField } from '../FileField'
import {
  InputStyled,
  LicenceAddButtonStyled,
  LicenceEmptyCopyStyled,
  LicenceListStyled,
  LicenceRemoveButtonStyled,
  LicenceRowStyled,
  RowStyled,
  SectionStyled,
  SelectStyled,
  SelectWrapStyled
} from '../onboardingStyles'
import { ICoachProfileFormValues } from '../types'

const STATE_OPTIONS = Object.values(AddressState)

const newRow = (uid: number) => ({
  uid,
  state: '' as AddressState | '',
  expirationDate: null,
  expirationReminderEnabled: true,
  file: null,
  existingDocumentUrl: null
})

const formatDateForInput = (date: Date | null): string => {
  if (!date) return ''
  const year = date.getFullYear().toString().padStart(4, '0')
  const month = (date.getMonth() + 1).toString().padStart(2, '0')
  const day = date.getDate().toString().padStart(2, '0')
  return `${year}-${month}-${day}`
}

export const StateLicensesSection = () => {
  const {
    control,
    register,
    formState: { errors }
  } = useFormContext<ICoachProfileFormValues>()

  const { fields, append, remove } = useFieldArray({ control, name: 'stateLicences' })

  // `useFieldArray.fields` is a snapshot — `state` on each entry stays at its initial
  // value after the user picks a new one. `useWatch` subscribes to live values so the
  // "already added" disabled options and the placeholder class track changes immediately.
  const liveRows = useWatch({ control, name: 'stateLicences' }) ?? []

  // `uid` is the stable identifier we use for the DOM ids of each row's inputs (so
  // `htmlFor` on the labels keeps pointing at the same element across renders even after
  // an earlier row is removed). RHF field names themselves are index-based.
  const nextUid = useMemo(() => {
    const maxUid = fields.reduce((max, row) => Math.max(max, row.uid ?? 0), 0)
    return maxUid + 1
  }, [fields])

  const usedStates = (exceptIndex: number): Set<string> =>
    new Set(
      liveRows
        .map((row, index) => (index === exceptIndex ? null : row?.state))
        .filter((value): value is AddressState => Boolean(value))
    )

  const usedAllStates = usedStates(-1).size >= STATE_OPTIONS.length

  return (
    <SectionStyled id="section-licenses">
      <div className="section-head">
        <div className="heading-block">
          <span className="num" aria-hidden="true">
            03
          </span>
          <div>
            <h2>State licenses</h2>
            <div className="blurb">
              States you&apos;re licensed to practice in, with each licence&apos;s expiration date
              and the licence image. You can add more later.
            </div>
          </div>
        </div>
        <span className="count" aria-live="polite">
          {fields.length} added
        </span>
      </div>

      {fields.length === 0 ? (
        <LicenceEmptyCopyStyled>
          No state licenses yet. This section is optional — add one if you hold an active state
          nutritionist license.
        </LicenceEmptyCopyStyled>
      ) : (
        <LicenceListStyled role="list">
          {fields.map((field, index) => {
            const used = usedStates(index)
            const currentState = liveRows[index]?.state ?? ''
            const rowErrors = errors.stateLicences?.[index]
            return (
              <LicenceRowStyled key={field.id} role="listitem">
                <div className="lic-row-head">
                  <span className="lic-row-title">
                    License <b>{index + 1}</b>
                  </span>
                  <LicenceRemoveButtonStyled
                    type="button"
                    aria-label="Remove this license"
                    onClick={() => remove(index)}
                  >
                    <CloseIcon sx={{ fontSize: 18 }} />
                  </LicenceRemoveButtonStyled>
                </div>

                <RowStyled>
                  <Field
                    htmlFor={`lic-state-${field.uid}`}
                    label="State"
                    required
                    error={rowErrors?.state?.message}
                  >
                    <SelectWrapStyled>
                      <SelectStyled
                        id={`lic-state-${field.uid}`}
                        className={currentState ? undefined : 'is-placeholder'}
                        aria-invalid={!!rowErrors?.state || undefined}
                        {...register(`stateLicences.${index}.state`, {
                          validate: (value) => (value ? undefined : 'Please choose a state.')
                        })}
                      >
                        <option value="">Select a state…</option>
                        {STATE_OPTIONS.map((state) => {
                          const disabled = used.has(state) && state !== currentState
                          return (
                            <option key={state} value={state} disabled={disabled}>
                              {state}
                              {disabled ? ' — already added' : ''}
                            </option>
                          )
                        })}
                      </SelectStyled>
                    </SelectWrapStyled>
                  </Field>

                  <Field
                    htmlFor={`lic-exp-${field.uid}`}
                    label="Expiration date"
                    required
                    error={rowErrors?.expirationDate?.message}
                  >
                    <Controller
                      control={control}
                      name={`stateLicences.${index}.expirationDate`}
                      rules={{
                        validate: (value) =>
                          validateFutureExpiration(value, 'Add the expiration date.')
                      }}
                      render={({ field: expField }) => (
                        <InputStyled
                          id={`lic-exp-${field.uid}`}
                          type="date"
                          aria-invalid={!!rowErrors?.expirationDate || undefined}
                          min={todayDateInputValue()}
                          value={formatDateForInput(expField.value)}
                          onBlur={expField.onBlur}
                          onChange={(event) => {
                            const next = event.target.value
                            expField.onChange(next ? new Date(`${next}T00:00:00`) : null)
                          }}
                        />
                      )}
                    />
                  </Field>
                </RowStyled>

                <Field
                  htmlFor={`lic-file-${field.uid}`}
                  label="License image"
                  required
                  error={rowErrors?.file?.message}
                >
                  <Controller
                    control={control}
                    name={`stateLicences.${index}.file`}
                    rules={{
                      validate: (value) =>
                        value || fields[index]?.existingDocumentUrl
                          ? undefined
                          : 'Please upload your license image.'
                    }}
                    render={({ field: fileField }) => (
                      <FileField
                        inputId={`lic-file-${field.uid}`}
                        accept="application/pdf,image/png,image/jpeg"
                        hint="PDF, PNG, JPG · up to 10MB"
                        icon="document"
                        file={fileField.value}
                        existingFileUrl={field.existingDocumentUrl ?? undefined}
                        invalid={!!rowErrors?.file}
                        onSelect={fileField.onChange}
                      />
                    )}
                  />
                </Field>
              </LicenceRowStyled>
            )
          })}
        </LicenceListStyled>
      )}

      <LicenceAddButtonStyled
        type="button"
        onClick={() => append(newRow(nextUid))}
        disabled={usedAllStates}
      >
        + {fields.length === 0 ? 'Add a state license' : 'Add another state license'}
      </LicenceAddButtonStyled>
    </SectionStyled>
  )
}
